/**
 * @jsxFrag
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import { defineMessages, useIntl } from 'react-intl-next';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import type { DocNode } from '@atlaskit/adf-schema';
import { cssMap } from '@atlaskit/css';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import { Stack } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers';
import { token } from '@atlaskit/tokens';
import {
	type AIAction,
	useAIActions,
	useAIAutomationsConfig,
	useAIContext,
	useAIConvoStarters,
	useAIProfileData,
	useAISurfaceMetadata,
	useViewAIActions,
} from '@atlassian/ai-smart-button-api';
import {
	useCanCreateAgents,
	useCreateAgentAtlasURL,
} from '@atlassian/conversation-assistant-agent';
import { type ConversationStarterType } from '@atlassian/convo-ai-api';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { useRovoButtonEventsInstrumentation } from '../../hooks/useRovoButtonEventsInstrumentation';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { AIDynamicSurfaceContext } from '../ai-dynamic-surface/AIDynamicSurfaceContext';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { DYNAMIC_SURFACE_PANEL_ID } from '../ai-dynamic-surface/useDynamicSurfacePanel';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { ObjectSidebar } from '../common/types/object-sidebar-types';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { AISmartButtonSidebarAPI } from '../common/types/shared-types';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { isObjectSidebar } from '../common/utils/ai-smart-button-shared';

import { DynamicSurfaceActionItem } from './components/action-item';
import { AIInput } from './components/ai-input';
import { ConvoStarterItem, ConvoStarterSkeleton } from './components/convo-starter-item';
import { FeedbackActionItem } from './components/feedback-item';
import { HeadingSection } from './components/heading-section';
import { MenuSection } from './components/menu/menu-section';
import { NoResults } from './components/no-results';
import { ConversationStarterTypeEnum, useFetchConvoStarters } from './hooks/useFetchConvoStarters';
import { useNavigateActions } from './hooks/useNavigateActions';
import { getStudioActions, type StudioAction } from './utils/get-studio-actions';

const sanitizeUrl = (url: string) => url.replace(/[^\w\-/.:%?=]+/g, '');

export const i18n = defineMessages({
	aiActionsSectionText: {
		id: 'ai-smart-button.ai-actions.actions.section.text',
		defaultMessage: 'Actions',
		description: 'The section text for the AI Actions in a Menu Group',
	},
	aiStudioSectionText: {
		id: 'ai-smart-button.ai-actions.rovo-agents.section.text',
		defaultMessage: 'Studio',
		description:
			'The section text for the AI Studio section (containing rovo agents) in a Menu Group',
	},
	aiHelpSectionText: {
		id: 'ai-smart-button.ai-actions.help.section.text',
		defaultMessage: 'Help',
		description: 'The section text for the Help Section in a Menu Group',
	},
	giveFeedbackActionTitle: {
		id: 'ai-smart-button.ai-actions.give-feedback-action.title',
		defaultMessage: 'Give feedback',
		description: 'The title for the give feedback action in the help section',
	},
	aiConvoStartersSectionText: {
		id: 'ai-smart-button.ai-actions.conversation-starters.section.text',
		defaultMessage: 'Ask Rovo',
		description: 'The section text of the Ask Rovo convo starter section',
	},
	convoStartersLoadingText: {
		id: 'ai-smart-button.ai-actions.conversation-starters.loading.text',
		defaultMessage: 'Loading conversation starters...',
		description: 'The text shown when the conversation starters are loading',
	},
});

const styles = cssMap({
	container: {
		height: '100%',
	},
	scrollContainer: {
		overflow: 'auto',
	},
});

type AIActionsProps = {
	name: string;
	/**
	 * Use sidebarApi instead. objectSidebar will be removed in a future release.
	 */
	sidebarApi: AISmartButtonSidebarAPI | ObjectSidebar;
};

type ConvoStarterAction = {
	agentInfo?: {
		id: string;
		identityAccountId?: string;
		name?: string;
		namedId?: string;
	};
	icon: React.JSX.Element;
	id: string;
	onClick: () => void;
	title: string;
	type: ConversationStarterType;
};

export const AIActions = ({ name, sidebarApi }: AIActionsProps) => {
	const usesLegacyObjectSidebar = isObjectSidebar(sidebarApi);

	const intl = useIntl();
	const { changeView } = useViewAIActions();
	const surfaceMetadata = useAISurfaceMetadata();
	const profileData = useAIProfileData();
	const automationsConfig = useAIAutomationsConfig();
	const actions = useAIActions();
	const context = useAIContext();
	const { activeAction, setActiveAction } = useContext(AIDynamicSurfaceContext);
	const { fireRovoButtonUIEvent } = useRovoButtonEventsInstrumentation();
	const showConvoStarters = useAIConvoStarters();
	const contentUrl = sanitizeUrl(document.location.href);
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	const [triggerId] = useState(() => uuid());

	const { isAbleToCreateAgents } = useCanCreateAgents({
		siteId: surfaceMetadata.siteId,
		touchpointSource: 'ai-smart-button-ai-actions',
	});

	/**
	 * If active action has children that render all children actions.
	 * AIDynamicSurface component will take care of showing back button in panel header.
	 */
	const isActionWithChildren = !!activeAction?.children;
	const selectedActions = !!activeAction?.children ? activeAction.children : actions;
	/**
	 * We want to hide the main AI actions for databases since they currently do not support AI features yet
	 */
	const isMainAiActionsShown = surfaceMetadata.contentType !== 'database';
	//Fire analytics events once AI Actions are finalized to record which analytics have been rendered to a user
	useEffect(() => {
		if (selectedActions.length === 0) {
			return;
		}

		const actionIds = selectedActions.filter((action) => action.id);

		fireRovoButtonUIEvent({
			source: surfaceMetadata.contentType,
			action: 'rendered',
			actionSubject: 'rovoButton',
			actionSubjectId: 'aiRovoDynamicSurfaceMenuItems',
			attributes: {
				product: surfaceMetadata.product,
				productPage: surfaceMetadata.contentType,
				pageMode: surfaceMetadata.pageMode,
				actions: actionIds,
			},
			product: surfaceMetadata.product,
		});
	}, [selectedActions, fireRovoButtonUIEvent, surfaceMetadata]);

	const [input, setInput] = useState('');

	const handleAction = useCallback(
		(action: AIAction | StudioAction | ConvoStarterAction) => {
			fireRovoButtonUIEvent({
				source: surfaceMetadata.contentType,
				action: 'clicked',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoDynamicSurfaceMenuItem',
				attributes: {
					pageMode: surfaceMetadata.pageMode,
					id: action.id,
					itemType: action.type ? action.type : 'action',
				},
				product: surfaceMetadata.product,
			});
			//if the user has typed something in the input field to filter menu actions, we want to fire an additional analytics event
			if (input.length) {
				fireRovoButtonUIEvent({
					source: surfaceMetadata.contentType,
					action: 'clicked',
					actionSubject: 'rovoButton',
					actionSubjectId: 'aiRovoDynamicSurfaceChatFilteredMenuItem',
					attributes: {
						product: surfaceMetadata.product,
						productPage: surfaceMetadata.contentType,
						pageMode: surfaceMetadata.pageMode,
						id: action.id,
						itemType: action.type ? action.type : 'action',
					},
					product: surfaceMetadata.product,
				});
			}
			/**
			 * If the action has children, set the active action so that
			 * we can display the children action
			 */
			if ('children' in action) {
				setActiveAction(action);
				if (usesLegacyObjectSidebar) {
					sidebarApi.actions.setActiveActionInPanel(action);
				}
			}
			if ('viewConfig' in action) {
				changeView(action);
				return;
			}
			if (action.onClick) {
				action.onClick();
			}
			if ('closePanelOnSelection' in action) {
				if (usesLegacyObjectSidebar) {
					sidebarApi.actions.hideObjectSidebarByPanelId(DYNAMIC_SURFACE_PANEL_ID);
				} else {
					sidebarApi.closePanel();
				}
			}
		},
		[
			changeView,
			setActiveAction,
			fireRovoButtonUIEvent,
			surfaceMetadata,
			input,
			sidebarApi,
			usesLegacyObjectSidebar,
		],
	);

	const debounceHandleAnalytics = useMemo(() => {
		const handleAnalytics = () => {
			fireRovoButtonUIEvent({
				source: surfaceMetadata.contentType,
				action: 'entered',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoDynamicSurfaceChatInput',
				attributes: {
					product: surfaceMetadata.product,
					productPage: surfaceMetadata.contentType,
					pageMode: surfaceMetadata.pageMode,
				},
				product: surfaceMetadata.product,
			});
		};
		return debounce(handleAnalytics, 1000);
	}, [fireRovoButtonUIEvent, surfaceMetadata]);

	const handleInput = useCallback(
		(text: string) => {
			setInput(text);
			debounceHandleAnalytics();
		},
		[debounceHandleAnalytics],
	);

	const filteredAIActions = useMemo(
		() =>
			selectedActions.filter(({ title }) => {
				return title.toLowerCase().includes(input.toLowerCase());
			}),
		[selectedActions, input],
	);

	const publish = usePublish('ai-mate');

	const createAgentUrl = useCreateAgentAtlasURL(surfaceMetadata?.siteId || '');
	const handleBrowseAgent = React.useCallback(() => {
		if (!surfaceMetadata) {
			return;
		}
		publish({
			type: 'open-browse-agent-sidebar',
			product: surfaceMetadata.product,
			source: 'ai-smart-button-dynamic-surface',
		});
	}, [publish, surfaceMetadata]);

	const studioActions = useMemo(() => {
		/**
		 * Don't show studio section when;
		 * - children action of active actions is shown.
		 * - when there is editor selection.
		 * Need to create separate studio actions and pass them along with
		 * 	consumption and editing actions.
		 * So that don't need to handle separate logic for studio actions.
		 */
		if (context?.reference || isActionWithChildren || !surfaceMetadata) {
			return [];
		}
		return getStudioActions({
			intl,
			createAgentUrl,
			handleBrowseAgent,
			renderUserAutomations: automationsConfig?.userAutomations?.render,
			onCreateAutomation: automationsConfig?.adminAutomations?.onClick,
			permissions: {
				canCreateAgents: isAbleToCreateAgents,
			},
		});
	}, [
		context?.reference,
		isActionWithChildren,
		surfaceMetadata,
		intl,
		createAgentUrl,
		handleBrowseAgent,
		automationsConfig?.userAutomations?.render,
		automationsConfig?.adminAutomations?.onClick,
		isAbleToCreateAgents,
	]);

	const filteredStudioActions = useMemo(
		() =>
			studioActions.filter(({ title }) => {
				return title.toLowerCase().includes(input.toLowerCase());
			}),
		[input, studioActions],
	);

	const { convoStarters: rovoConvoStarters, isLoading: isConvoStartersLoading } =
		useFetchConvoStarters({
			showConvoStarters,
			triggerId,
			numberOfRecommendations: 2,
			contentUrl,
		});

	const convoStarters = useMemo(
		() =>
			rovoConvoStarters
				.filter((starter) => starter !== null)
				.map((starter) => ({
					id: starter.agent_name || starter.starter,
					title: starter.starter,
					icon: (
						<AiChatIcon
							label=""
							color={
								starter.type === ConversationStarterTypeEnum.AGENT
									? token('color.icon.brand')
									: undefined
							}
						/>
					),
					type: starter.type as ConversationStarterType,
					agentInfo: starter.agent_id
						? {
								name: starter.agent_name,
								id: starter.agent_id,
								namedId: starter.agent_named_id,
								identityAccountId: starter.agent_identity_account_id,
							}
						: undefined,
					onClick: () => {
						publish({
							type: 'chat-new',
							product: surfaceMetadata?.product,
							source: 'ai-smart-button-dynamic-surface',
							data: {
								name: starter.agent_name || starter.starter,
								dialogues: [],
								prompt: starter.starter,
								agentId: starter.agent_id,
							},
						});

						fireRovoButtonUIEvent({
							source: surfaceMetadata.contentType,
							action: 'clicked',
							actionSubject: 'rovoButton',
							actionSubjectId: 'aiRovoDynamicSurfaceContextualConvoStarter',
							attributes: {
								product: surfaceMetadata.product,
								productPage: surfaceMetadata.contentType,
								pageMode: surfaceMetadata.pageMode,
								convoStarterMessage: starter.starter,
								convoStarterType: starter.type,
							},
							product: surfaceMetadata.product,
						});
					},
				}))
				.slice(0, 2),
		[rovoConvoStarters, publish, surfaceMetadata, fireRovoButtonUIEvent],
	);

	//analytics events to check number/type of convo starters initially rendered
	useEffect(() => {
		if (!showConvoStarters || isConvoStartersLoading || convoStarters.length === 0) {
			return;
		}

		const allTypes = Object.values(ConversationStarterTypeEnum);
		const typeCounts = allTypes.reduce<Record<ConversationStarterType, number>>(
			(acc, type) => {
				acc[type] = 0;
				return acc;
			},
			{} as Record<ConversationStarterType, number>,
		);

		convoStarters.forEach((starter) => {
			if (typeCounts.hasOwnProperty(starter.type)) {
				typeCounts[starter.type]++;
			}
		});

		fireRovoButtonUIEvent({
			source: surfaceMetadata.contentType,
			action: 'rendered',
			actionSubject: 'rovoButton',
			actionSubjectId: 'aiRovoDynamicSurfaceConvoStarters',
			attributes: {
				product: surfaceMetadata.product,
				productPage: surfaceMetadata.contentType,
				pageMode: surfaceMetadata.pageMode,
				...typeCounts,
			},
			product: surfaceMetadata.product,
		});
	}, [
		convoStarters,
		surfaceMetadata,
		fireRovoButtonUIEvent,
		isConvoStartersLoading,
		showConvoStarters,
	]);

	const filteredConvoStarters = useMemo(
		() =>
			convoStarters.filter(
				(starter) => starter && starter.title.toLowerCase().includes(input.toLowerCase()),
			),
		[input, convoStarters],
	);

	const helpActions = useMemo(
		() => [
			{
				component: (
					<FeedbackActionItem surfaceMetadata={surfaceMetadata} profileData={profileData} />
				),
				title: intl.formatMessage(i18n.giveFeedbackActionTitle),
			},
		],
		[surfaceMetadata, profileData, intl],
	);

	const filteredHelpActions = useMemo(
		() =>
			helpActions.filter(({ title }) => {
				return title.toLowerCase().includes(input.toLowerCase());
			}),
		[input, helpActions],
	);

	const filteredFullList = useMemo(
		() =>
			([] as (AIAction | StudioAction | ConvoStarterAction)[]).concat(
				filteredAIActions,
				filteredStudioActions,
				filteredConvoStarters,
			),
		[filteredAIActions, filteredStudioActions, filteredConvoStarters],
	);

	const filteredFullListValues = useMemo(
		() => filteredFullList.map(({ id }) => id),
		[filteredFullList],
	);

	const { handleArrowDown, handleArrowUp, selectedIndex } = useNavigateActions(
		filteredFullListValues.length,
	);

	const handleSelect = useCallback(
		(adf: DocNode) => {
			if (filteredFullList.length && selectedIndex !== undefined) {
				const filteredAction = filteredFullList?.[selectedIndex];
				if (filteredAction) {
					handleAction(filteredAction);
				}
			} else if (input.length) {
				publish({
					type: 'chat-new',
					product: surfaceMetadata?.product,
					source: 'ai-smart-button-dynamic-surface',
					data: {
						name: input,
						dialogues: [],
						prompt: adf,
					},
				});
				fireRovoButtonUIEvent({
					source: surfaceMetadata.contentType,
					action: 'invoked',
					actionSubject: 'rovoButton',
					actionSubjectId: 'aiRovoDynamicSurfaceChatInput',
					attributes: {
						pageMode: surfaceMetadata.pageMode,
					},
					product: surfaceMetadata.product,
				});
			}
		},
		[
			filteredFullList,
			selectedIndex,
			handleAction,
			input,
			publish,
			surfaceMetadata,
			fireRovoButtonUIEvent,
		],
	);

	const handleBack = useCallback(() => {
		if (!activeAction) {
			return;
		}
		setActiveAction(undefined);
	}, [setActiveAction, activeAction]);

	const renderConvoStarters = useCallback(() => {
		if (isConvoStartersLoading) {
			return <ConvoStarterSkeleton numberOfItems={2} />;
		} else {
			return (
				<>
					{filteredConvoStarters.map((starter, index) => (
						<ConvoStarterItem
							key={starter.id}
							title={starter.title}
							icon={starter.icon}
							isSelected={selectedIndex === filteredAIActions.length + index}
							onClick={starter.onClick}
							type={starter.type}
							agentInfo={starter.agentInfo}
						/>
					))}
				</>
			);
		}
	}, [isConvoStartersLoading, filteredConvoStarters, selectedIndex, filteredAIActions.length]);

	return (
		<Stack grow="fill" xcss={styles.container}>
			{filteredFullList.length || filteredHelpActions.length ? (
				<>
					<Stack grow="fill" xcss={styles.scrollContainer}>
						<>
							{!isActionWithChildren && <HeadingSection name={name} />}
							{filteredAIActions.length && isMainAiActionsShown ? (
								<MenuSection
									heading={
										isActionWithChildren
											? activeAction.title
											: intl.formatMessage(i18n.aiActionsSectionText)
									}
								>
									{filteredAIActions.map((action) => (
										<DynamicSurfaceActionItem
											key={action.id}
											onClick={() => handleAction(action)}
											title={action.title}
											icon={action.icon}
											status={action.status}
											ref={action.ref}
										/>
									))}
								</MenuSection>
							) : null}
							{showConvoStarters && (filteredConvoStarters.length || isConvoStartersLoading) ? (
								<MenuSection heading={intl.formatMessage(i18n.aiConvoStartersSectionText)}>
									{renderConvoStarters()}
								</MenuSection>
							) : null}
							{filteredStudioActions.length ? (
								<MenuSection heading={intl.formatMessage(i18n.aiStudioSectionText)}>
									{filteredStudioActions.map((action) => (
										<DynamicSurfaceActionItem
											key={action.id}
											title={action.title}
											icon={action.icon}
											{...('href' in action && action?.href
												? {
														href: action.href,
														onClick: () => handleAction(action),
													}
												: {
														onClick: () => handleAction(action),
													})}
										/>
									))}
								</MenuSection>
							) : null}
							{filteredHelpActions.length ? (
								<MenuSection heading={intl.formatMessage(i18n.aiHelpSectionText)}>
									{filteredHelpActions.map(({ component }, index) => (
										<React.Fragment key={index}>{component}</React.Fragment>
									))}
								</MenuSection>
							) : null}
						</>
					</Stack>
				</>
			) : (
				<Stack alignInline="center" grow="fill">
					<NoResults />
				</Stack>
			)}
			<AIInput
				surfaceMetadata={surfaceMetadata}
				onSelect={handleSelect}
				onInputChange={handleInput}
				handleArrowDown={handleArrowDown}
				handleArrowUp={handleArrowUp}
				handleBack={handleBack}
			/>
		</Stack>
	);
};
